import React from 'react';
import classnames from 'classnames';
import moment from 'moment';
import { Section, FieldGrid } from '../../components/EntityList';
import { formatDateString } from '../../components/FormattedDateTime/FormattedDateTime';
import { useLabels } from '../../hooks/useLabels';
import { getMeta, FRONTEND_LEGACY_DEBT_META, behaviorNeedsCapital, behaviorNeedsDues } from '../../utils/conceptMeta';

function formatDate(date) {
	return date ? moment.utc(date).format('DD/MM/YYYY') : null;
}

/**
 * Read-only Sí/No indicator for `hasPreviousDebts` — this is a detail view,
 * not the editor, so the selected segment is only ever visual, never a real
 * `<input type=radio>` pair.
 */
function YesNoIndicator({ value }) {
	return (
		<div className='tw:inline-flex tw:overflow-hidden tw:rounded-full tw:border tw:border-border'>
			<span className={classnames('tw:px-2.5 tw:py-0.5 tw:text-[11px] tw:font-semibold', value ? 'tw:bg-accent tw:text-white' : 'tw:bg-surface-2 tw:text-ink-3')}>
				Sí
			</span>
			<span className={classnames('tw:px-2.5 tw:py-0.5 tw:text-[11px] tw:font-semibold', !value ? 'tw:bg-accent tw:text-white' : 'tw:bg-surface-2 tw:text-ink-3')}>
				No
			</span>
		</div>
	);
}

function DebtInfo({ debt }) {
	const { t } = useLabels();

	if (!debt || !debt._id) {
		return null;
	}

	// Behavior snapshot taken at creation is the source of truth; fall back to
	// the legacy slug map for older debts that predate the snapshot. Neither
	// path repeats the accented/unaccented ('interés'/'interes') literal-string
	// bug the old concept.includes()/!== checks had.
	const behavior = debt.behavior || getMeta(debt.concept, undefined, FRONTEND_LEGACY_DEBT_META).behavior;
	const needsCapital = behaviorNeedsCapital(behavior);
	const needsDues = behaviorNeedsDues(behavior);
	const isInterestOnly = behavior === 'interest-only';

	const generationLeft = [
		{ label: t('form.debtStartDateLong'), value: formatDateString({ dateValue: debt.date, dateFormat: 'DD/MM/YYYY' }), required: true },
		{ label: t('form.firstDueDayLong'), value: debt.dueDay },
		{ label: t('form.generateFrom'), value: debt.selectedMonthOption },
		...(debt.selectedMonthOption === 'Otro' ? [{ label: t('form.inHowManyMonths'), value: debt.numMonths }] : []),
	];

	const generationRight = [
		{ label: t('msg.firstExpiration'), value: formatDate(debt.firstDueExpiration) },
	];

	const infoLeft = [
		{ label: t('entity.concept'), value: debt.concept },
		{ label: t('entity.account.debtor'), value: debt.owner?.name, required: true },
		...(debt.concept === 'alquileres' && debt.contract
			? [{ label: `${t('field.contract')}:`, value: debt.contract.customId || debt.contract._id }]
			: []),
		...(debt.concept === 'ventas-a-plazo' && debt.assetSale
			? [{ label: `${t('entity.assetSale')}:`, value: debt.assetSale.customId || debt.assetSale._id }]
			: []),
		{ label: t('field.currency'), value: debt.currency, mono: true },
		{ label: t('field.periodicity'), value: debt.periodicity },
		...(debt.periodicity === 'otra' ? [{ label: t('form.periodicityMonths'), value: debt.periodicityNumMonths }] : []),
		...(debt.concept === 'alquileres' && debt.closeDate
			? [{ label: t('form.contractCloseDate'), value: formatDate(debt.closeDate) }]
			: []),
	];

	const infoRight = [
		...(needsCapital ? [{ label: t('field.capital'), value: debt.capital, mono: true }] : []),
		...(needsCapital ? [{ label: t('form.interestPercent'), value: debt.interest, mono: true }] : []),
		...(isInterestOnly ? [{ label: t('form.endDate'), value: formatDate(debt.endDate) }] : []),
		...(needsDues ? [{ label: t('entity.due.plural'), value: debt.dues }] : []),
		{
			label: `${t('field.amount')} ${['amortizable', 'interest-only'].includes(behavior) ? 'cuota' : ''}`.trim(),
			value: debt.amount,
			mono: true,
		},
	];

	const offSystemLeft = [
		{ label: t('form.hasPreviousDebtQ'), value: <YesNoIndicator value={!!debt.hasPreviousDebts} /> },
		{ label: t('field.previousDebt'), value: debt.previousDebt, mono: true },
	];

	const offSystemRight = [
		{ label: t('form.previousDebtDate'), value: formatDate(debt.previousDebtDate) },
	];

	return (
		<div className='tw:flex tw:flex-col tw:gap-4'>
			{debt.createWithPreviousDues && (
				<div className='tw:rounded-panel tw:border tw:border-border tw:bg-warn-soft tw:px-4 tw:py-3 tw:text-[13px] tw:text-warn'>
					<b>
						{t('title.debtWithPreviousDues')} {debt.createWithPreviousPayments ? 'y pagas' : ''}
					</b>
					<p className='tw:m-0 tw:mt-1'>{t('msg.debtCreatedWithPreviousDues')}</p>
				</div>
			)}

			<Section title={t('title.dueGenerationData')}>
				<FieldGrid left={generationLeft} right={generationRight} />
			</Section>

			<Section title={t('title.accountingInfo')}>
				<FieldGrid left={infoLeft} right={infoRight} />
			</Section>

			<Section title={t('title.offSystemRecord')} description={t('msg.offSystemTooltip')}>
				<FieldGrid left={offSystemLeft} right={offSystemRight} />
			</Section>
		</div>
	);
}

export default DebtInfo;
